import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Descriptions,
  Typography,
  Popconfirm,
  Input,
  Alert,
  Card,
  Row,
  Col,
  Divider
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  WarningOutlined,
  CalendarOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';
import { bookingAPI } from '../../utils/api';
import { Booking } from '../../types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface BookingsManagementProps {
  active: string;
}

interface OverlappingBooking {
  id: string;
  packageName: string;
  customerName: string;
  date: string;
  numberOfPeople: number;
  phone: string;
  email: string;
}

const BookingsManagement: React.FC<BookingsManagementProps> = ({ active }) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  
  // Overlap detection states
  const [overlapModalVisible, setOverlapModalVisible] = useState<boolean>(false);
  const [overlappingBookings, setOverlappingBookings] = useState<OverlappingBooking[]>([]);
  const [bookingToAccept, setBookingToAccept] = useState<Booking | null>(null);
  const [acceptLoading, setAcceptLoading] = useState<boolean>(false);
  
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (active === 'bookings' && !hasFetchedRef.current) {
      fetchBookings();
      hasFetchedRef.current = true;
    }
  }, [active]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getAll();
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      message.error('Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (booking: Booking) => {
    try {
      setAcceptLoading(true);
      setBookingToAccept(booking);
      
      // Try to accept without forcing
      // eslint-disable-next-line
      const response = await bookingAPI.accept(booking._id as any, false);
      
      // Success - no overlap
      message.success('Booking accepted successfully');
      fetchBookings();
      
    } catch (error: any) {
      console.error('Error accepting booking:', error);
      
      // Check if it's an overlap error (409 Conflict)
      if (error.response?.status === 409 && error.response?.data?.code === 'BOOKING_OVERLAP') {
        // Show overlap warning modal
        setOverlappingBookings(error.response.data.overlappingBookings || []);
        setOverlapModalVisible(true);
      } else {
        // Other error
        message.error(error.response?.data?.message || 'Failed to accept booking');
      }
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleForceAccept = async () => {
    if (!bookingToAccept) return;
    
    try {
      setAcceptLoading(true);
      
      // Accept with force flag
      await bookingAPI.accept(bookingToAccept._id as any, true);
      
      message.success('Booking accepted (overlaps confirmed)');
      setOverlapModalVisible(false);
      setBookingToAccept(null);
      setOverlappingBookings([]);
      fetchBookings();
      
    } catch (error: any) {
      console.error('Error force accepting booking:', error);
      message.error(error.response?.data?.message || 'Failed to accept booking');
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleCancelOverlap = () => {
    setOverlapModalVisible(false);
    setBookingToAccept(null);
    setOverlappingBookings([]);
  };

  const handleCancel = async (id: string) => {
    try {
      await bookingAPI.cancel(id);
      message.success('Booking cancelled successfully');
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      message.error('Failed to cancel booking');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await bookingAPI.delete(id);
      message.success('Booking deleted successfully');
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      message.error('Failed to delete booking');
    }
  };

  const showDetails = (booking: Booking) => {
    setSelectedBooking(booking);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'accepted':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  const filteredBookings = (bookings || []).filter(booking =>
    booking.customerName?.toLowerCase().includes(searchText.toLowerCase()) ||
    booking.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    booking.packageName?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Booking ID',
      dataIndex: '_id',
      key: '_id',
      render: (text: any) => <span>{text.substring(0, 8)}...</span>,
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Package',
      dataIndex: 'packageName',
      key: 'packageName',
    },
    {
      title: 'Type',
      dataIndex: 'bookingType',
      key: 'bookingType',
      render: (type: any) => (
        <Tag color={type === 'package' ? 'blue' : 'purple'}>
          {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Trip Date',
      key: 'date',
      render: (_: any, record: any) => (
        <span>
          {dayjs(record.date).format('MMM DD, YYYY')}
        </span>
      ),
    },
    {
      title: 'People',
      dataIndex: 'numberOfPeople',
      key: 'numberOfPeople',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: any) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showDetails(record)}
          >
            View
          </Button>
          {record.status === 'pending' && (
            <>
              <Button
                type="link"
                icon={<CheckOutlined />}
                onClick={() => handleAccept(record)}
                loading={acceptLoading && bookingToAccept?._id === record._id}
                style={{ color: 'green' }}
              >
                Accept
              </Button>
              <Button
                type="link"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleCancel(record._id)}
              >
                Reject
              </Button>
            </>
          )}
          {record.status === 'accepted' && (
            <Button
              type="link"
              danger
              icon={<CloseOutlined />}
              onClick={() => handleCancel(record._id)}
            >
              Cancel
            </Button>
          )}
          <Popconfirm
            title="Are you sure you want to delete this booking?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Title level={3} style={{ margin: 0 }}>One-Day Trip Bookings</Title>
        <Input
          placeholder="Search by customer, email, or package"
          prefix={<SearchOutlined />}
          style={{ width: '100%', maxWidth: 400 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredBookings}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      {/* Booking Details Modal */}
      <Modal
        title="Booking Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width="95%"
        style={{ maxWidth: 700 }}
      >
        {selectedBooking && (
          <Descriptions
            bordered
            column={{ xs: 1, sm: 1, md: 2 }}
          >
            <Descriptions.Item label="Booking ID" span={2}>
              {selectedBooking._id}
            </Descriptions.Item>
            <Descriptions.Item label="Customer Name" span={2}>
              {selectedBooking.customerName}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedBooking.email}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {selectedBooking.phone}
            </Descriptions.Item>
            <Descriptions.Item label="Package">
              {selectedBooking.packageName}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={selectedBooking.bookingType === 'package' ? 'blue' : 'purple'}>
                {selectedBooking.bookingType.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Trip Date" span={2}>
              {dayjs(selectedBooking.date).format('MMMM DD, YYYY (dddd)')}
            </Descriptions.Item>
            <Descriptions.Item label="Number of People">
              {selectedBooking.numberOfPeople}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              ${selectedBooking.totalAmount}
            </Descriptions.Item>
            {selectedBooking.destinations && (
              <Descriptions.Item label="Destinations" span={2}>
                {selectedBooking.destinations}
              </Descriptions.Item>
            )}
            {selectedBooking.specialRequests && (
              <Descriptions.Item label="Special Requests" span={2}>
                {selectedBooking.specialRequests}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedBooking.status)}>
                {selectedBooking.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {dayjs(selectedBooking.createdAt).format('MMMM DD, YYYY HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Overlap Warning Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14', fontSize: 20 }} />
            <span>Booking Overlap Detected</span>
          </Space>
        }
        open={overlapModalVisible}
        onCancel={handleCancelOverlap}
        footer={[
          <Button key="cancel" onClick={handleCancelOverlap}>
            Cancel
          </Button>,
          <Button
            key="accept"
            type="primary"
            danger
            icon={<CheckOutlined />}
            onClick={handleForceAccept}
            loading={acceptLoading}
          >
            Accept Anyway
          </Button>,
        ]}
        width={800}
      >
        {bookingToAccept && (
          <div>
            {/* Warning Alert */}
            <Alert
              message={`Warning: This booking overlaps with ${overlappingBookings.length} existing confirmed booking(s) on the same day`}
              description="Accepting this booking may cause scheduling conflicts. Please review the overlapping bookings below before proceeding."
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 20 }}
            />

            {/* Booking to Accept */}
            <Card
              title={
                <Space>
                  <CheckOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Booking to Accept:</Text>
                </Space>
              }
              style={{ marginBottom: 20 }}
              headStyle={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">
                      <UserOutlined /> Customer
                    </Text>
                    <Text strong>{bookingToAccept.customerName}</Text>
                  </Space>
                </Col>
                <Col xs={24} sm={12}>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">
                      <CalendarOutlined /> Package
                    </Text>
                    <Text strong>{bookingToAccept.packageName}</Text>
                  </Space>
                </Col>
                <Col xs={24} sm={12}>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">
                      <CalendarOutlined /> Trip Date
                    </Text>
                    <Text strong>
                      {dayjs(bookingToAccept.date).format('MMM DD, YYYY (dddd)')}
                    </Text>
                  </Space>
                </Col>
                <Col xs={24} sm={12}>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary">
                      <UserOutlined /> Number of People
                    </Text>
                    <Text strong>{bookingToAccept.numberOfPeople}</Text>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Overlapping Bookings */}
            <div>
              <Title level={5} style={{ color: '#ff4d4f', marginBottom: 16 }}>
                <WarningOutlined /> Conflicting Bookings ({overlappingBookings.length}):
              </Title>
              
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {overlappingBookings.map((overlap, index) => (
                  <Card
                    key={overlap.id}
                    size="small"
                    style={{ borderColor: '#ff4d4f' }}
                    headStyle={{ backgroundColor: '#fff1f0' }}
                    title={
                      <Text strong style={{ color: '#ff4d4f' }}>
                        Booking #{index + 1}
                      </Text>
                    }
                  >
                    <Row gutter={[12, 12]}>
                      <Col xs={24} sm={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <UserOutlined /> Customer
                          </Text>
                          <Text>{overlap.customerName}</Text>
                        </Space>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Package
                          </Text>
                          <Text>{overlap.packageName}</Text>
                        </Space>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <CalendarOutlined /> Trip Date
                          </Text>
                          <Text>
                            {dayjs(overlap.date).format('MMM DD, YYYY (dddd)')}
                          </Text>
                        </Space>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <UserOutlined /> People
                          </Text>
                          <Text>{overlap.numberOfPeople} people</Text>
                        </Space>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <PhoneOutlined /> Phone
                          </Text>
                          <Text>{overlap.phone}</Text>
                        </Space>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <MailOutlined /> Email
                          </Text>
                          <Text>{overlap.email}</Text>
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            </div>

            <Divider />

            {/* Action Options */}
            <Alert
              message="What would you like to do?"
              description={
                <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                  <li>Cancel and contact the customer to reschedule</li>
                  <li>Accept anyway if you have capacity (multiple guides/vehicles)</li>
                  <li>Review and adjust other bookings if needed</li>
                </ul>
              }
              type="info"
              showIcon
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BookingsManagement;
